import React, { ReactNode, useCallback, useMemo, useState } from 'react';

import { useApp } from '../../hooks';
import { ActivityTotals } from '../ActivityTotals/ActivityTotals';
import { FitnessSettingsModal } from '../FitnessSettings/FitnessSettingsModal/FitnessSettingsModal';
import { StopSharingConfirmation } from '../FitnessSettings/StopSharingConfirmation/StopSharingConfirmation';
import { ManualFitnessEntryModal } from '../ManualFitnessEntry/ManualFitnessEntryModal/ManualFitnessEntryModal';
import { childComponentSelector } from './childComponentSelector';
import { FitnessLogo } from './FitnessLogo';
import { FitnessManagerContext } from './FitnessManagerContext';
import {
  AddManualActivityButton,
  ConnectButton,
  FitnessManagerLayout,
  FitnessManagerLayoutProps,
  SettingsButton,
} from './FitnessManagerLayout';
import { FitnessApplicationName } from './useFitnessConnectionSettings';

type FitnessManagerProps = FitnessManagerLayoutProps & {
  fitnessApplicationName: FitnessApplicationName;
};

export const FitnessManager = ({
  fitnessApplicationName,
  children,
  ...rest
}: FitnessManagerProps) => {
  const modalManager = useModalManager(
    useMemo(() => childComponentSelector(children, FitnessSettingsModal), [
      children,
    ]),
    useMemo(() => childComponentSelector(children, ManualFitnessEntryModal), [
      children,
    ]),
  );

  return (
    <FitnessManagerContext
      modalManager={modalManager}
      fitnessApplicationName={fitnessApplicationName}
    >
      <FitnessManagerLayout {...rest}>{children}</FitnessManagerLayout>
    </FitnessManagerContext>
  );
};

export type ModalType = 'settings' | 'confirmation' | 'addManualActivity';

function useModalManager(
  settingsModal: ReactNode,
  addManualActivityModal: ReactNode,
) {
  const { hideToast } = useApp();
  const [modal, setModal] = useState<ReactNode | null>(null);

  const handleCloseModal = () => setModal(null);

  const handleOpenModal = useCallback(
    (modalType: ModalType) => {
      hideToast();
      switch (modalType) {
        case 'settings':
          setModal(settingsModal);
          break;
        case 'confirmation':
          setModal(<StopSharingConfirmation />);
          break;
        case 'addManualActivity':
          setModal(addManualActivityModal);
          break;
        default:
          setModal(null);
      }
    },
    [settingsModal, addManualActivityModal, hideToast],
  );

  const hasCookieConsent =
    typeof window !== 'undefined'
      ? document.cookie.includes('JgCookieNotice')
      : false;

  return {
    status: {
      hasCookieConsent,
    },
    actions: {
      onCloseModal: handleCloseModal,
      onOpenModal: handleOpenModal,
    },
    modal,
  };
}

FitnessManager.SettingsButton = SettingsButton;
FitnessManager.ConnectButton = ConnectButton;
FitnessManager.AddManualActivityButton = AddManualActivityButton;
FitnessManager.Settings = FitnessSettingsModal;
FitnessManager.AddManualActivity = ManualFitnessEntryModal;
FitnessManager.ActivityTotals = ActivityTotals;
FitnessManager.FitnessLogo = FitnessLogo;
